/**
 * Удаление клиента агентства: что происходит, чего человек не увидит и почему
 * это отдельный файл.
 *
 * Отдельно от экрана по той же причине, что и гейт оформления, и разбор отказов
 * заведения: здесь живут решения, у которых есть правильный и неправильный
 * ответ, а глазами их не проверить. Разработчик не видит ни клиента с пятью
 * живыми брендами, ни второй вкладки, в которой этот клиент уже удалён.
 *
 * Три решения, ради которых всё это написано.
 *
 * 1. **Ведущий диалог вместо разрушительной кнопки.** Пока у клиента есть хоть
 *    один живой бренд, сервер отвечает `409` и ничего не делает
 *    (`services/workspaces.py`: пространство архивируется ПУСТЫМ). Показывать в
 *    этом состоянии красную кнопку «Удалить клиента» — это обещание, которое
 *    отобьёт сервер: человек нажимает, получает отказ и остаётся с ним один на
 *    один. Поэтому диалог здесь другой: он называет бренды поимённо и ведёт
 *    туда, где их убирают. Разрушительного действия в нём нет вовсе.
 * 2. **Область работы не остаётся стоять на удалённом клиенте.** Пространство
 *    выбрано в переключателе наверху, и после удаления все экраны спрашивали бы
 *    данные пространства, которого для API больше нет, — это `404` на каждом
 *    втором запросе вместо объяснения. Выбор сбрасывается ровно тогда, когда
 *    удалён именно он: чужой выбор трогать незачем.
 * 3. **Ключа идемпотентности нет, и это не забывчивость.** Повтор не заводит
 *    ничего второго: он упирается в `409` от сервера. Заведение клиента — прямо
 *    противоположный случай, и там ключ живёт всю попытку (`agency-create.ts`).
 *
 * Слова «архив» здесь нет, хотя ручка называется `archive`. Ленты архивных
 * клиентов в продукте нет, вернуть клиента самостоятельно нельзя — для
 * покупателя это удаление, и называть его надо так, как он его понимает
 * (`voice.md`: «Name things the way a buyer would»).
 */

import { archiveWorkspace, type AgencyClient } from '../api/team';
import { countOf } from '@shared/src/format';

/** Как клиент назван на экране: своим именем, если оно задано. */
export function clientTitle(client: AgencyClient): string {
  return client.client_display_name || client.name;
}

/**
 * Есть ли у клиента живые бренды.
 *
 * `client.brands` — это ровно неархивные бренды пространства: сервер кладёт в
 * сводку только их (`pg_agency.read_brands_of`: `archived_at IS NULL`). То есть
 * это тот же предикат, по которому сервер отбивает архивацию пространства
 * `409`, а не догадка кабинета о его правилах. Поля с числом брендов у
 * `AgencyClient` нет — оно есть у схемы `Workspace`, а это другая выдача.
 */
export function clientRemoveBlocked(client: AgencyClient): boolean {
  return client.brands.length > 0;
}

/** Какое окно показать: ведущее или подтверждение удаления. */
export function clientRemoveDialog(client: AgencyClient): 'blocked' | 'confirm' {
  return clientRemoveBlocked(client) ? 'blocked' : 'confirm';
}

export interface RemoveClientDeps {
  /**
   * Какое пространство выбрано ПРЯМО СЕЙЧАС — функцией, а не значением.
   *
   * Снимок на момент щелчка здесь врёт: окно закрывается первой строкой, запрос
   * летит, и за это время человек успевает переключиться на другое
   * пространство. Ответ, пришедший следом, сбросил бы уже НОВЫЙ выбор —
   * человека выкинуло бы из места, куда он только что перешёл, и виноватым
   * выглядело бы переключение, а не удаление.
   */
  selectedWorkspaceId: () => string | null;
  /** Сбросить выбранное пространство: `scope.selectWorkspace(null)`. */
  dropSelection: () => void;
  /** Перечитать область: переключатель, список пространств, квота. */
  reload: () => void;
}

export interface ClientBrandsDeps {
  /** Перевести область на пространство клиента. */
  selectWorkspace: (workspaceId: string) => void;
  /** Уйти на экран: `navigate` маршрутизатора. */
  navigate: (path: string) => void;
}

/**
 * Выход из ведущего окна: к брендам того клиента, о котором шла речь.
 *
 * Порядок обязателен. Экран брендов показывает бренды ВЫБРАННОГО пространства,
 * и уход туда без смены области привёл бы человека к чужим брендам — он искал
 * бы там «Автодом», которого в его текущем пространстве нет, и решил бы, что
 * окно соврало.
 */
export function openClientBrands(client: AgencyClient, deps: ClientBrandsDeps): void {
  deps.selectWorkspace(client.workspace_id);
  deps.navigate('/brands');
}

/**
 * Показывать ли на карточке клиента ход в архив его брендов.
 *
 * Есть ровно одно состояние, из которого до архива клиента не дойти ничем:
 * живых брендов не осталось, а в архиве что-то лежит. Переключатель наверху
 * перечисляет бренды и пропускает пространства без живых, сводка клиента живых
 * тоже не содержит — выбрать такого клиента областью нечем, а вкладка «Архив»
 * показывает архив ВЫБРАННОГО пространства. Кнопка «Удалить» архивного бренда
 * живёт только в этой вкладке, то есть клиент, честно убравший все бренды из
 * работы, оказывался заперт: история есть, дойти до неё нельзя.
 *
 * Клиенту с живыми брендами ход не нужен: он выбирается переключателем, и
 * вкладка открывается сама. Клиенту без брендов вовсе — тем более: показывать
 * пустой архив значит обещать содержимое там, где его нет.
 *
 * Число архивных считает область (`archivedCountOf`), а не эта функция: список
 * брендов организации лежит у неё, и мерка живости обязана быть одна.
 */
export function clientArchiveOnly(client: AgencyClient, archived: number): boolean {
  return client.brands.length === 0 && archived > 0;
}

/**
 * Удалить клиента и привести кабинет в порядок после этого.
 *
 * Порядок важен: сначала ответ сервера, потом уборка. Сброс выбора до ответа
 * увёл бы человека из пространства, которое осталось на месте, — на любом отказе
 * (`409` про живые бренды, обрыв связи) экран обязан выглядеть так, будто ничего
 * не нажимали.
 *
 * Возвращает строку для полосы «Готово»: исчезновение карточки без единого
 * слова читается как сбой, особенно когда список перечитывается не мгновенно.
 * Отказ отсюда пробрасывается как есть — его текст пишет сервер, он знает число
 * живых брендов.
 *
 * Про возврат вопросов говорится только тогда, когда возвращать есть что: у
 * клиента без выделенной квоты — а таким он и появляется на свет — «его вопросы
 * вернулись» обещает движение, которого не было. Подтверждение выше молчит об
 * этом по той же причине, и расходиться этим двум текстам нельзя.
 */
export async function removeClient(
  client: AgencyClient,
  deps: RemoveClientDeps,
): Promise<string> {
  await archiveWorkspace(client.workspace_id);
  if (deps.selectedWorkspaceId() === client.workspace_id) deps.dropSelection();
  deps.reload();
  const tracked = client.allocation?.tracked_questions ?? 0;
  return tracked > 0
    ? `${clientTitle(client)} — пространство удалено, его вопросы вернулись в общую квоту.`
    : `${clientTitle(client)} — пространство удалено.`;
}

/**
 * Что именно исчезнет. Показывается только тому клиенту, у которого живых
 * брендов не осталось: остальным показывается окно ниже.
 */
export function ClientRemoveBody({ client }: { client: AgencyClient }) {
  const tracked = client.allocation?.tracked_questions ?? 0;
  return (
    <>
      <p className="dlg-lead">
        Пространство клиента «{clientTitle(client)}» исчезнет из списка
        {tracked > 0
          ? `, а выделенные ему ${countOf(tracked, 'вопрос', 'вопроса', 'вопросов')} вернутся в квоту тарифа`
          : ''}
        . Место в лимите тарифа тоже освободится.
      </p>
      <p className="dlg-lead">
        История замеров сохранится, но открыть её из кабинета будет нельзя. Вернуть
        клиента самостоятельно не получится — если удалили по ошибке, напишите в
        поддержку.
      </p>
    </>
  );
}

/**
 * Почему удалить нельзя и что сделать вместо этого.
 *
 * Бренды называются поимённо, а не числом: «сначала уберите 3 бренда» оставляет
 * человека искать, какие именно, — а их у клиента может быть и восемь, и часть
 * из них он уже убрал.
 */
export function ClientHasBrandsBody({ client }: { client: AgencyClient }) {
  const names = client.brands.map((brand) => `«${brand.brand_name}»`).join(', ');
  // Согласование по СМЫСЛУ, а не по грамматической категории числа: у «21 бренд»
  // категория та же, что у «1 бренд», и `pluralize` дал бы «пока он жив» про
  // двадцать один бренд. Существительное считает `countOf`, а сказуемое — вот
  // это сравнение.
  const one = client.brands.length === 1;
  return (
    <>
      <p className="dlg-lead">
        У клиента «{clientTitle(client)}» ещё {one ? 'замеряется' : 'замеряются'}{' '}
        {countOf(client.brands.length, 'бренд', 'бренда', 'брендов')}: {names}.{' '}
        {one ? 'Пока он жив, его' : 'Пока они живы, их'} вопросы занимают квоту
        организации, а замеры по расписанию продолжают идти.
      </p>
      {/* Названо тем словом, которое человек увидит на кнопке, когда придёт по
          этому указанию. На строках списка брендов и в карточке стоит
          «Удалить», «Архивировать» есть только внутри карточки — и человек,
          посланный архивировать, ищет на экране брендов слово, которого там
          нет. Продукт называет вещи так, как их называет покупатель, и одним
          словом во всех местах сразу (`voice.md`). */}
      <p className="dlg-lead">
        Сначала удалите каждый бренд — потом можно будет убрать и клиента.
      </p>
    </>
  );
}
